export type FlattenFieldOption = {
  /** 写入 columns.dataIndex 的值，如 report.equipmentName */
  key: string;
  /** 下拉展示 */
  label: string;
  group?: 'root' | 'nested' | 'array';
  valueType?: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null' | 'unknown';
};

export type FlattenObjectPathsOptions = {
  maxDepth?: number;
  skipKeys?: Set<string>;
  arraySampleStrategy?: 'first';
};

const DEFAULT_SKIP_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function detectValueType(value: unknown): FlattenFieldOption['valueType'] {
  if (value === null) return 'null';
  if (value === undefined) return 'unknown';
  if (Array.isArray(value)) return 'array';
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return t;
  if (t === 'object') return 'object';
  return 'unknown';
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pushUnique(out: FlattenFieldOption[], seen: Set<string>, item: FlattenFieldOption) {
  if (seen.has(item.key)) return;
  seen.add(item.key);
  out.push(item);
}

function flattenValue(
  value: unknown,
  path: string,
  depth: number,
  maxDepth: number,
  skipKeys: Set<string>,
  out: FlattenFieldOption[],
  seen: Set<string>,
) {
  if (depth > maxDepth) return;

  const group: FlattenFieldOption['group'] =
    path.includes('.') ? (path.includes('.0.') || path.endsWith('.0') ? 'array' : 'nested') : 'root';

  if (value === null || value === undefined) {
    if (path) {
      pushUnique(out, seen, {
        key: path,
        label: path,
        group,
        valueType: value === null ? 'null' : 'unknown',
      });
    }
    return;
  }

  if (Array.isArray(value)) {
    if (path) {
      pushUnique(out, seen, {
        key: path,
        label: path,
        group: 'array',
        valueType: 'array',
      });
    }
    if (value.length === 0) return;
    const first = value[0];
    if (isPlainObject(first)) {
      const prefix = path ? `${path}.0` : '0';
      const sampleSuffix = value.length > 1 ? ' (首项采样)' : '';
      Object.keys(first).forEach((childKey) => {
        if (skipKeys.has(childKey)) return;
        const childPath = `${prefix}.${childKey}`;
        const childVal = first[childKey];
        if (isPlainObject(childVal) || (Array.isArray(childVal) && childVal.some(isPlainObject))) {
          flattenValue(childVal, childPath, depth + 1, maxDepth, skipKeys, out, seen);
        } else {
          pushUnique(out, seen, {
            key: childPath,
            label: `${childPath}${sampleSuffix}`,
            group: 'array',
            valueType: detectValueType(childVal),
          });
        }
      });
    }
    return;
  }

  if (!isPlainObject(value)) {
    if (path) {
      pushUnique(out, seen, {
        key: path,
        label: path,
        group,
        valueType: detectValueType(value),
      });
    }
    return;
  }

  Object.keys(value).forEach((key) => {
    if (skipKeys.has(key)) return;
    const childPath = path ? `${path}.${key}` : key;
    const childVal = value[key];
    if (isPlainObject(childVal) || Array.isArray(childVal)) {
      flattenValue(childVal, childPath, depth + 1, maxDepth, skipKeys, out, seen);
    } else {
      pushUnique(out, seen, {
        key: childPath,
        label: childPath,
        group: childPath.includes('.') ? 'nested' : 'root',
        valueType: detectValueType(childVal),
      });
    }
  });
}

/**
 * 从样例行递归展开叶子字段路径，供设计器 AndTable dataIndex 下拉使用。
 * 扁平行仅产出根级 key，与旧 Object.keys 结果集合一致。
 */
export function flattenObjectPaths(
  sampleRow: Record<string, unknown>,
  options?: FlattenObjectPathsOptions,
): FlattenFieldOption[] {
  if (!sampleRow || typeof sampleRow !== 'object') return [];
  const maxDepth = options?.maxDepth ?? 4;
  const skipKeys = options?.skipKeys ?? DEFAULT_SKIP_KEYS;
  const out: FlattenFieldOption[] = [];
  const seen = new Set<string>();
  flattenValue(sampleRow, '', 0, maxDepth, skipKeys, out, seen);
  return out;
}
